import { useState } from 'react';
import { AdminMonth, confirmMonth } from '../api/adminApi';
import styles from './ConfirmSightingModal.module.css';

// ─── Confirm Start Date mode ────────────────────────────────────────────────
// Used when day=0: admin enters the official start date of the current month.

// ─── Sighting Night mode ─────────────────────────────────────────────────────
// Used on day 29: admin records whether crescent was seen.
// If seen  → confirms the START of next month.
// If not seen → nothing to record (app auto-handles day 30), just close.

export type ModalMode =
  | { type: 'CONFIRM_START'; month: AdminMonth }
  | { type: 'SIGHTING_NIGHT'; currentMonth: AdminMonth; nextMonth: AdminMonth | null };

interface Props {
  mode: ModalMode;
  locationCode: string;
  authority: string;
  onClose: () => void;
  onSuccess: () => void;
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

export default function ConfirmSightingModal({ mode, locationCode, authority, onClose, onSuccess }: Props) {
  const [sightingResult, setSightingResult] = useState<'SEEN' | 'NOT_SEEN' | null>(null);
  const [startDate, setStartDate] = useState(todayIso());
  const [proofUrlsText, setProofUrlsText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmStart = mode.type === 'CONFIRM_START';
  const isSightingNight = mode.type === 'SIGHTING_NIGHT';
  // Narrowed reference — safe to access SIGHTING_NIGHT fields
  const sightingMode = mode.type === 'SIGHTING_NIGHT' ? mode : null;

  // The month whose start date we're confirming
  const targetMonth: AdminMonth | null = isConfirmStart
    ? mode.month
    : sightingResult === 'SEEN'
    ? (sightingMode?.nextMonth ?? null)
    : null;

  const canSubmit = isConfirmStart
    ? !!startDate
    : sightingResult === 'NOT_SEEN' || (sightingResult === 'SEEN' && !!startDate && !!targetMonth);

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const proofUrls = proofUrlsText
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);

    try {
      if (isConfirmStart) {
        await confirmMonth({
          locationCode: locationCode,
          year: mode.month.year,
          month: mode.month.monthIndex,
          startDate,
          source: authority,
          ...(proofUrls.length > 0 && { proofUrls }),
        });
        onSuccess();
      } else if (isSightingNight) {
        if (sightingResult === 'NOT_SEEN') {
          // No API call needed — app handles day 30 automatically
          onSuccess();
        } else if (sightingResult === 'SEEN' && targetMonth) {
          await confirmMonth({
            locationCode: locationCode,
            year: targetMonth.year,
            month: targetMonth.monthIndex,
            startDate,
            source: authority,
            ...(proofUrls.length > 0 && { proofUrls }),
          });
          onSuccess();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {isConfirmStart
                ? `Confirm ${mode.month.monthName.en} ${mode.month.year}`
                : `Moon Sighting — ${mode.currentMonth.monthName.en} Day 29`}
            </h2>
            <p className={styles.subtitle}>
              {isConfirmStart
                ? 'Enter the official start date from the announcement.'
                : 'Record tonight\'s moon sighting result.'}
            </p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16">
              <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* ── Sighting Night: Was it seen? ── */}
          {isSightingNight && (
            <div className={styles.sightingChoices}>
              <button
                className={`${styles.choice} ${sightingResult === 'SEEN' ? styles.choiceSeen : ''}`}
                onClick={() => setSightingResult('SEEN')}
              >
                <span className={styles.choiceIcon}>🌙</span>
                <span className={styles.choiceLabel}>Crescent was seen</span>
                <span className={styles.choiceSub}>
                  {sightingMode?.nextMonth
                    ? `${sightingMode.nextMonth.monthName.en} starts tomorrow`
                    : 'New month starts tomorrow'}
                </span>
              </button>
              <button
                className={`${styles.choice} ${sightingResult === 'NOT_SEEN' ? styles.choiceNotSeen : ''}`}
                onClick={() => setSightingResult('NOT_SEEN')}
              >
                <span className={styles.choiceIcon}>☁️</span>
                <span className={styles.choiceLabel}>Crescent not seen</span>
                <span className={styles.choiceSub}>
                  {sightingMode?.currentMonth.monthName.en} continues to day 30
                </span>
              </button>
            </div>
          )}

          {/* ── Date picker (shown for CONFIRM_START or SEEN) ── */}
          {(isConfirmStart || sightingResult === 'SEEN') && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {isConfirmStart
                  ? `When did ${mode.month.monthName.en} officially start?`
                  : `When does ${targetMonth?.monthName.en ?? 'the new month'} start?`}
              </label>
              <input
                type="date"
                className={styles.dateInput}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          )}

          {/* ── Source (always Morocco) ── */}
          {(isConfirmStart || sightingResult === 'SEEN') && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Official source</label>
              <div className={styles.sourceDisplay}>
                <svg viewBox="0 0 16 16" fill="currentColor" className={styles.sourceIcon}>
                  <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm11.03-1.47a.75.75 0 010 1.06l-3.25 3.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 011.06-1.06l.97.97 2.72-2.72a.75.75 0 011.06 0z" clipRule="evenodd"/>
                </svg>
                {authority}
              </div>
            </div>
          )}

          {/* ── Proof URLs (optional) ── */}
          {(isConfirmStart || sightingResult === 'SEEN') && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Proof links <span className={styles.fieldOptional}>(optional — one URL per line)</span>
              </label>
              <textarea
                className={styles.proofUrls}
                value={proofUrlsText}
                onChange={(e) => setProofUrlsText(e.target.value)}
                placeholder="https://www.habous.gov.ma/..."
                rows={2}
              />
            </div>
          )}

          {/* ── Not seen confirmation message ── */}
          {sightingResult === 'NOT_SEEN' && (
            <div className={styles.notSeenInfo}>
              <svg viewBox="0 0 16 16" fill="currentColor" className={styles.infoIcon}>
                <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1a.75.75 0 01.75.75v2.75h.25a.75.75 0 010 1.5h-2a.75.75 0 010-1.5h.25v-2h-.25a.75.75 0 01-.75-.75zM8 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
              </svg>
              <p>
                The app will automatically show day 30 tomorrow. Once the crescent is seen
                the following night, come back to confirm the start of {sightingMode?.nextMonth?.monthName.en ?? 'the next month'}.
              </p>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Saving…' : sightingResult === 'NOT_SEEN' ? 'Got it' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
